// @flow

// @todo Extract this to a separate npm module…
// But make sorting of parameters optional.

import * as Immutable            from 'immutable';
import type { StringifyOptions } from 'query-string';
import qs                        from 'query-string';
import nodeUrl                   from 'url';

const {
        fromJS,
        isImmutable,
        List,
        Map,
      } = Immutable;


/**
 * Append own methods to the base url module.
 */
const url = Object.assign({}, nodeUrl, {

  /**
   * Adds query values to a URL
   *
   * @param {string}       urlString             URL string to add the parameters to.
   * @param {object}       newParams             Query params to be added as either key: value or key: [value,value].
   * @param options…
   * @param {boolean}      [options.overwrite]   Flag to indicate whether to overwrite the existing parameter.
   *                                             Default: true
   * @param {string|null}  [options.arrayFormat] How to parse multiple parameter entries.
   *                                             Possible values: 'bracket', 'index', null
   *                                             Default: 'bracket'
   * @param {boolean|null} [options.encode]      Whether to encode the final query string.
   *                                             Default: true
   */
  addQueryParams: (
                    urlString: string,
                    newParams: {
                      [string]: string | number | Array<string | number> | { [string]: string | number }
                    } = {},
                    options: {
                      overwrite?: boolean,
                      ...StringifyOptions
                    } = {}) => {

    const overwrite: boolean = undefined === options.overwrite ? true : Boolean(options.overwrite);

    const qsOptions: StringifyOptions = {
      arrayFormat: options.arrayFormat ? options.arrayFormat : 'bracket',
      encode:      Boolean(options.encode),
      strict:      true,
    };


    // @todo $FlowFixMe For some reason parseUrl is not in the module's flow-type declarations
    let {url, query} = qs.parseUrl(urlString, qsOptions);

    // convert to immutables

    const newParamsImmutable: Map<string, *> =
            // $FlowFixMe fromJS does not return the correct type
            fromJS(newParams);
    let queryImmutable: Map<string, *>       =
            // $FlowFixMe fromJS does not return the correct type
            fromJS(query);

    if (overwrite) {
      queryImmutable = newParamsImmutable
        .reduce((queryImmutable, value, key) => queryImmutable.remove(key), queryImmutable);
    }

    queryImmutable = queryImmutable.mergeDeepWith((oldVal, newVal, key) => {

      if ((isImmutable(oldVal) && !isImmutable(newVal)) || (!isImmutable(oldVal) && isImmutable(newVal))) {

        throw new Error(
          'Unable to merge single values with multiple values.  You should manually remove the old parameters before ' +
          'adding the new ones',
        );

      }

      // If overwrite = `true` then the oldVal would have already been removed, and replace, therefore we need to keep
      // it.
      return oldVal;

    }, newParamsImmutable);

    // Sort any array of multi-value parameters.
    queryImmutable = queryImmutable.map((value) => {

      if (!isImmutable(value)) {

        return value;

      }

      return value.sort();

    });

    return `${url}?${qs.stringify(queryImmutable.toJS(), qsOptions)}`;

  },


  /**
   * Removed params from a URL string.
   *
   * @param {string}      urlString             The URL to strip the params from.
   * @param {object}      paramsToRemove        Map of params to remove.  Consists of the param name as the key and
   *                                            either a string value that will be matched with the param value before
   *                                            removing the parameter, or `true` to remove the parameter, whatever it's
   *                                            value.
   * @param options ...
   * @param {string|null} [options.arrayFormat] How to parse multiple parameter entries.
   *                                            Possible values: 'bracket', 'index', null
   *                                            Default: 'bracket'
   * @param {boolean}     [options.encode]      Whether to encode the query string.
   *                                            Default: true
   *
   * @return {string} Te string without the requested parameters.
   */
  removeQueryParams: (
                       urlString: string,
                       paramsToRemove: { [string]: * },
                       options: { ...StringifyOptions } = {}) => {

    const qsOptions: StringifyOptions = {
      arrayFormat: 'bracket',
      encode:      true,
      ...options,
    };

    // @todo $FlowFixMe For some reason parseUrl is not in the module's flow-type declarations
    let {url, query} = qs.parseUrl(urlString, qsOptions);

    // Convert to immutables

    let queryImmutable: Map<string, string | List<string>>                   =
          // $FlowFixMe fromJS does not return the correct type
          fromJS(query);
    const paramsToRemoveImmutable: Map<string, List<string> | string | true> =
          // $FlowFixMe fromJS does not return the correct type
          fromJS(paramsToRemove);

    // Remove parameters…

    queryImmutable = paramsToRemoveImmutable.reduce(
      (queryImmutable, values, key) => {

        if (!queryImmutable.has(key)) {

          return queryImmutable;

        }

        // If true, remove all params by this name
        if (true === values) {

          queryImmutable = queryImmutable.remove(key);

          return queryImmutable;

        }

        // If only a single value, convert to an iterable List…
        if ('string' === typeof values) {

          values = List([values]);

        }

        values = (values: List<string>);

        queryImmutable = values.reduce((queryImmutable, valueToRemove) => {

          const currentQueryValues = queryImmutable.get(key);

          if (!currentQueryValues) {
            return queryImmutable;
          }

          if ('string' === typeof currentQueryValues) {
            return queryImmutable.remove(key);
          }

          const index = currentQueryValues.indexOf(valueToRemove);

          if (-1 === index) {
            return queryImmutable;
          }

          return queryImmutable.set(key, currentQueryValues.delete(index));

        }, queryImmutable);

        return queryImmutable;

      },
      queryImmutable,
    );

    return `${url}?${qs.stringify(queryImmutable.toJS(), qsOptions)}`
    // Remove any unnecessary trailing '?'
      .replace(/.\?$/, '');

  },

});


export default url;



// WEBPACK FOOTER //
// ./src/utils/url.js